import React, { useCallback, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { ContentfulImage, type ContentfulImageTransformOptions } from '@garnish/media';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFeaturedUpsellConfettiPiece = (props: Props) => {
  const {
    imageUrl,
    containerWidth,
    containerHeight,
    origin,
    drift,
    peak,
    size,
    delay,
    riseDuration,
    fallDuration,
    rotation,
  } = props;

  // ─── Shared Values ─────────────────────────────────────────────────────────

  const phase = useSharedValue(0);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const burst = useCallback(() => {
    phase.value = withDelay(
      delay,
      withSequence(
        withTiming(1, { duration: riseDuration, easing: Easing.out(Easing.cubic) }),
        withTiming(2, { duration: fallDuration, easing: Easing.in(Easing.sin) }),
      ),
    );
  }, [delay, riseDuration, fallDuration, phase]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(burst, [burst]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const positionStyle = useStyle(
    () => ({ width: size, height: size, left: `${origin}%` as const }),
    [origin, size],
  );

  const animatedStyle = useAnimatedStyle(() => {
    const rise = Math.min(phase.value, 1);
    const fall = Math.max(phase.value - 1, 0);
    const peakHeight = peak * containerHeight;

    return {
      transform: [
        { translateX: drift * containerWidth * rise },
        { translateY: peakHeight * (fall - rise) },
        { rotate: `${(rotation * phase.value) / 2}deg` },
        { scale: interpolate(phase.value, [0, 0.2], [0.4, 1], Extrapolation.CLAMP) },
      ],
    };
  });

  const itemStyles = [styles.item, positionStyle, animatedStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Animated.View style={itemStyles}>
      <ContentfulImage
        baseUrl={imageUrl}
        options={IMAGE_OPTIONS}
        contentFit="contain"
        style={styles.image}
      />
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_OPTIONS: Partial<ContentfulImageTransformOptions> = { fm: 'webp', w: 64 };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  item: {
    position: 'absolute',
    top: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  imageUrl: string;
  containerWidth: number;
  containerHeight: number;
  origin: number;
  drift: number;
  peak: number;
  size: number;
  delay: number;
  riseDuration: number;
  fallDuration: number;
  rotation: number;
};
